import { MainContent, Template } from 'components';
import { useSubmitAuthInputData } from 'hooks/useSubmitAuthInputData';
import React, { useRef, useState } from 'react';
import { FormPage } from 'shared';
import { scrollAndFocus } from 'utils';
import {
  HANDLED_SUBMISSION_RESULT_STATE_STATUS,
  useFormErrors,
  useHandleSubmissionResult,
  useInitPage,
} from '../../hooks';
import { ReCaptchaGlobalStyles } from '../shared';
import { AuthContent, AuthContentProps } from './AuthContent';

export const Auth: React.FC = () => {
  const { reCaptchaSetting, currentFormPage, mainContentRef, globalErrors } =
    useInitPage();
  const [submitting, setSubmitting] = useState(false);
  const { formErrors, resetFormErrors, updateFormErrors } = useFormErrors();
  const globalErrorRef = useRef<HTMLDivElement>(null);

  const { submit } = useSubmitAuthInputData({
    reCaptchaSetting,
  });
  const { handleSubmissionResult } = useHandleSubmissionResult();

  const handleSubmissionClick: AuthContentProps['onSubmissionClick'] = async ({
    endpoint,
    values,
  }) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    resetFormErrors();
    const updatedInputData = {
      [pageId]: values,
    };

    const result = await submit({
      endpoint,
      inputData: updatedInputData,
    });
    const state = handleSubmissionResult(result);
    setSubmitting(false);
    const {
      unrecoverableError,
      unauthorizedError,
      submissionError,
      validationError,
      success,
    } = HANDLED_SUBMISSION_RESULT_STATE_STATUS;
    if (state.status === unrecoverableError || state.status === success) {
      return;
    }
    if (state.status === submissionError) {
      const { submission } = state.error;
      updateFormErrors({
        submission,
      });
      return;
    }

    if (state.status === validationError) {
      const { global } = state.error;
      updateFormErrors({
        global,
      });
      scrollAndFocus(globalErrorRef.current);
      return;
    }

    if (state.status === unauthorizedError) {
      scrollAndFocus(globalErrorRef.current);
      return;
    }
  };

  if (!currentFormPage) {
    return null;
  }
  const displayAlternateTextForReCaptcha = reCaptchaSetting?.hideBadge || false;
  const { design, header, footer, title, message, pageId, pageType } =
    currentFormPage;

  const updateGlobalError = (
    errorMessage: FormPage['invalidInputError']['authenticationFailMessage'] = ''
  ): void => {
    updateFormErrors({
      global: [errorMessage],
    });
    scrollAndFocus(globalErrorRef.current);
  };

  return (
    <>
      <ReCaptchaGlobalStyles reCaptchaSetting={reCaptchaSetting} />
      <Template
        header={header}
        footer={footer}
        design={design}
        displayAlternateTextForReCaptcha={displayAlternateTextForReCaptcha}
      >
        <MainContent
          title={title}
          message={message}
          pageType={pageType}
          pageId={pageId}
          design={design}
          ref={mainContentRef}
        >
          <AuthContent
            formPage={currentFormPage}
            submitting={submitting}
            onSubmissionClick={handleSubmissionClick}
            updateGlobalError={updateGlobalError}
            errors={formErrors}
            ref={globalErrorRef}
            globalErrors={globalErrors}
          />
        </MainContent>
      </Template>
    </>
  );
};
